/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { callOpenRouterApi } from '../../services/openrouterApi';
import { UserProfile, ChatMessage as ChatMessageType } from '../../types';
import { getConfusionSolverPrompt } from '../../utils/prompts';
import { supabase } from '../../services/supabase';
import ChatMessage from '../common/ChatMessage';
import { validateLaTeX } from '../../utils/mathUtils';
import { debounce } from 'lodash';
import { useTTS } from '../../contexts/TTSContext';
import InputField from '../common/InputField'; // Import the InputField component
import '../../styles/components/ConfusionSolver.css';

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  isTemporary?: boolean;
}

interface ConfusionSolverProps {
  userProfile: UserProfile;
  sessionId?: string;
}

const ConfusionSolver: React.FC<ConfusionSolverProps> = ({ userProfile, sessionId }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [customAreaOfStudy, setCustomAreaOfStudy] = useState('');
  const [otherAreaOfStudy, setOtherAreaOfStudy] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  const { speak, isMessageSpeaking } = useTTS();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat sessions from database on mount
  useEffect(() => {
    const loadSessions = async () => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setErrorMessage('Failed to authenticate user. Please log in again.');
        return;
      }

      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('chat_type', 'confusion-solver')
        .order('created_at', { ascending: false });

      if (error) {
        setErrorMessage('Error loading sessions: ' + error.message);
        console.error('Error loading sessions:', error);
        return;
      }

      const loadedSessions: ChatSession[] = await Promise.all(
        data.map(async (session) => {
          const { data: msgs, error: msgError } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('session_id', session.id)
            .order('created_at', { ascending: true });

          if (msgError) {
            setErrorMessage('Error loading messages for session ' + session.id + ': ' + msgError.message);
            console.error('Error loading messages:', msgError);
          }

          return {
            id: session.id,
            title: session.title,
            timestamp: new Date(session.created_at),
            messages: msgs?.map(msg => ({
              id: msg.id?.toString() || Date.now().toString(),
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.created_at),
              chat_type: 'confusion-solver',
            })) || [],
            isTemporary: false,
          };
        })
      );

      setSessions(loadedSessions);

      if (loadedSessions.length > 0) {
        const initialSession = sessionId 
          ? loadedSessions.find(s => s.id === sessionId) || loadedSessions[0]
          : loadedSessions[0];
        setCurrentSessionId(initialSession.id);
        setMessages(initialSession.messages);
      } else {
        const tempSession: ChatSession = {
          id: `temp-${Date.now()}`,
          title: 'New Confusion Solver Chat',
          timestamp: new Date(),
          messages: [],
          isTemporary: true,
        };
        setSessions([tempSession]);
        setCurrentSessionId(tempSession.id);
        setMessages([]);
      }
    };

    loadSessions();
  }, [sessionId]);

  // Generate chat title from first message
  const generateChatTitle = (firstMessage: string): string => {
    const words = firstMessage.trim().split(' ').slice(0, 6);
    return words.join(' ') + (words.length < firstMessage.split(' ').length ? '...' : '');
  };

  // Save chat session to database
  const saveChatSession = async (session: ChatSession) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      setErrorMessage('Failed to authenticate user. Please log in again.');
      return null;
    }

    const { data, error } = await supabase
      .from('chat_sessions')
      .insert({
        user_id: user.id,
        title: session.title,
        chat_type: 'confusion-solver',
        created_at: session.timestamp.toISOString(),
      })
      .select()
      .single();

    if (error) {
      setErrorMessage('Error creating chat session: ' + error.message);
      console.error('Error creating chat session:', error);
      return null;
    }

    return { ...session, id: data.id, isTemporary: false };
  };

  // Save chat message to database
  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    if (!sessionId) {
      setErrorMessage('No active session. Please start a new chat.');
      return null;
    }

    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        session_id: sessionId,
        role,
        content,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      setErrorMessage('Error saving message: ' + error.message);
      console.error('Error saving message:', error);
      return null;
    }

    return data;
  };

  // Update chat title in database and local state
  const updateChatTitle = async (sessionId: string, newTitle: string) => {
    if (!sessionId) return;

    const { error } = await supabase
      .from('chat_sessions')
      .update({ title: newTitle })
      .eq('id', sessionId);

    if (error) {
      setErrorMessage('Error updating title: ' + error.message);
      console.error('Error updating title:', error);
      return;
    }

    setSessions(prev => prev.map(session => 
      session.id === sessionId 
        ? { ...session, title: newTitle }
        : session
    ));
  };

  // Get effective area of study
  const getEffectiveAreaOfStudy = () => {
    if (customAreaOfStudy === 'Other' && otherAreaOfStudy.trim()) {
      return otherAreaOfStudy.trim();
    }
    return customAreaOfStudy || userProfile.area_of_study;
  };

  // Handle edit message
  const handleEditMessage = async (messageId: string, newContent: string) => {
    const { error } = await supabase
      .from('chat_messages')
      .update({ content: newContent })
      .eq('id', messageId);

    if (error) {
      setErrorMessage('Error updating message: ' + error.message);
      console.error('Error updating message:', error);
      return;
    }

    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, content: newContent } : msg
    ));

    setSessions(prev => prev.map(session => 
      session.id === currentSessionId 
        ? { ...session, messages: session.messages.map(msg => 
            msg.id === messageId ? { ...msg, content: newContent } : msg
          ) }
        : session
    ));
  };

  // Handle try again
  const handleTryAgain = async (messageId: string) => {
    const messageToRetry = messages.find(msg => msg.id === messageId);
    if (!messageToRetry || messageToRetry.role !== 'assistant') return;

    const userMessage = messages[messages.indexOf(messageToRetry) - 1];
    if (!userMessage) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const effectiveAreaOfStudy = getEffectiveAreaOfStudy();
      const fullPrompt = getConfusionSolverPrompt(userProfile, userMessage.content, effectiveAreaOfStudy);
      
      const systemMessage = `You are a Math Explainer AI.
Structure your response with:
1. Step-by-step solution
2. Real-world application example
3. Common mistakes to avoid
4. A practice problem for the student to solve`;

      const response = await callOpenRouterApi(fullPrompt, userProfile, systemMessage);
      const finalResponse = validateLaTeX(response);

      const newAssistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalResponse,
        timestamp: new Date(),
        chat_type: 'confusion-solver',
      };

      const updatedMessages = [...messages.filter(msg => msg.id !== messageId), newAssistantMessage]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setMessages(updatedMessages);

      setSessions(prev => prev.map(session => 
        session.id === currentSessionId 
          ? { ...session, messages: updatedMessages }
          : session
      ));

      if (!currentSessionId.startsWith('temp-')) {
        await supabase
          .from('chat_messages')
          .delete()
          .eq('id', messageId);

        await saveChatMessage(currentSessionId, 'assistant', finalResponse);
      }
    } catch (error) {
      console.error('Error retrying message:', error);
      setErrorMessage('Failed to regenerate response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle like/dislike
  const handleLike = async (messageId: string) => {
    console.log(`Liked message ${messageId}`);
    // Add logic to store like status if needed
  };

  const handleDislike = async (messageId: string) => {
    console.log(`Disliked message ${messageId}`);
    // Add logic to store dislike status if needed
  };

  // Handle share
  const handleShare = async (messageId: string) => {
    try {
      const shareUrl = `${window.location.origin}/message/${messageId}`;
      await navigator.clipboard.writeText(shareUrl);
      console.log('Share link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy share link:', err);
      setErrorMessage('Failed to copy share link.');
    }
  };

  // Handle export PDF
  const handleExportPDF = async (messageId: string) => {
    const message = messages.find(msg => msg.id === messageId);
    if (!message) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const cleanContent = message.content
        .replace(/\$\$([^$]+)\$\$/g, '$1')
        .replace(/\$([^$]+)\$/g, '$1')
        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 divided by $2')
        .replace(/\\sqrt\{([^}]+)\}/g, 'square root of $1')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/#{1,6}\s*(.+)/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

      printWindow.document.write(`
        <html>
          <head>
            <title>Message Export</title>
            <style>
              body { font-family: Arial, sans-serif; max-width: 800px; margin: 20px auto; padding: 20px; }
              .message { background: #f8f9fa; padding: 15px; border-radius: 8px; }
              .timestamp { color: #666; font-size: 12px; }
            </style>
          </head>
          <body>
            <h3>${message.role === 'user' ? 'User Message' : 'AI Response'}</h3>
            <div class="message">${cleanContent}</div>
            <p class="timestamp">Generated on: ${message.timestamp.toLocaleString()}</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Handle message submission
  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    setErrorMessage('');

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      chat_type: 'confusion-solver',
    };

    let newSessionId = currentSessionId;
    let isNewSession = messages.length === 0 && sessions.find(s => s.id === currentSessionId)?.isTemporary;

    if (isNewSession) {
      const tempSession = sessions.find(s => s.id === currentSessionId);
      if (tempSession) {
        const savedSession = await saveChatSession(tempSession);
        if (!savedSession) {
          setMessages(messages);
          setIsLoading(false);
          return;
        }
        newSessionId = savedSession.id;
        setSessions(prev => prev.map(s => s.id === currentSessionId ? savedSession : s));
        setCurrentSessionId(newSessionId);
      }
    }

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    if (messages.length === 0) {
      const title = generateChatTitle(input);
      await updateChatTitle(newSessionId, title);
    }

    setSessions(prev => prev.map(session => 
      session.id === currentSessionId 
        ? { ...session, messages: newMessages }
        : session
    ));

    const savedMessage = await saveChatMessage(newSessionId, 'user', input);
    if (!savedMessage) {
      setMessages(messages);
      setIsLoading(false);
      return;
    }

    try {
      const effectiveAreaOfStudy = getEffectiveAreaOfStudy();
      const fullPrompt = getConfusionSolverPrompt(userProfile, input, effectiveAreaOfStudy);
      
      const systemMessage = `You are a Math Explainer AI.
- Use clear step-by-step explanations
- Use PROPER LaTeX syntax
- Use SIMPLE LaTeX commands only
- Include real-world applications from ${userProfile.country}, ${userProfile.state}
- Provide a practice problem at the end
- Use tables for comparisons when helpful

Structure your response with:
1. Step-by-step solution
2. Real-world application example
3. Common mistakes to avoid
4. A practice problem for the student to solve`;

      const response = await callOpenRouterApi(fullPrompt, userProfile, systemMessage);
      const finalResponse = validateLaTeX(response);

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalResponse,
        timestamp: new Date(),
        chat_type: 'confusion-solver',
      };

      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);

      setSessions(prev => prev.map(session => 
        session.id === newSessionId 
          ? { ...session, messages: updatedMessages }
          : session
      ));

      const savedAssistantMessage = await saveChatMessage(newSessionId, 'assistant', finalResponse);
      if (!savedAssistantMessage) {
        setMessages(newMessages);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error while processing your request. Please try again in a moment. If this persists, check your API key configuration.',
        timestamp: new Date(),
        chat_type: 'confusion-solver',
      };
      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);

      setSessions(prev => prev.map(session => 
        session.id === newSessionId 
          ? { ...session, messages: updatedMessages }
          : session
      ));

      await saveChatMessage(newSessionId, 'assistant', errorMessage.content);
    } finally {
      setInput('');
      setIsLoading(false);
    }
  };

  // Create new chat session (debounced)
  const createNewChat = useCallback(
    debounce(async () => {
      const tempSession: ChatSession = {
        id: `temp-${Date.now()}`,
        title: 'New Confusion Solver Chat',
        timestamp: new Date(),
        messages: [],
        isTemporary: true,
      };

      setSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSession.id);
      setMessages([]);
      setErrorMessage('');
    }, 300),
    []
  );

  // Switch chat session
  const switchToSession = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSessionId(sessionId);
      setMessages(session.messages);
      setErrorMessage('');
    }
  };

  // Delete chat session
  const deleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.isTemporary) {
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } else {
      const { error: msgError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('session_id', sessionId);

      if (msgError) {
        setErrorMessage('Error deleting messages: ' + msgError.message);
        console.error('Error deleting messages:', msgError);
      }

      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        setErrorMessage('Error deleting session: ' + error.message);
        console.error('Error deleting session:', error);
      }

      setSessions(prev => prev.filter(s => s.id !== sessionId));
    }

    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        switchToSession(remainingSessions[0].id);
      } else {
        createNewChat();
      }
    }
  };

  // Filter sessions based on search
  const filteredSessions = sessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <style>
        {`
          .confusion-solver-wrapper {
            position: relative;
            overflow: hidden;
          }
          .background-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            z-index: -1;
          }
          .morning-sky {
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, #87CEEB, #E0F6FF);
          }
          .night-sky {
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, #001f3f, #000);
          }
          .confusion-solver-wrapper.light {
            color: black;
          }
          .confusion-solver-wrapper.dark {
            color: white;
          }
          .chatbot-header {
            background: transparent !important;
            text-align: center;
            padding: 10px 0;
          }
          .main-content {
            background: transparent;
          }
          .sidebar {
            background: rgba(0, 0, 0, 0.5);
          }
          .theme-toggle-sidebar {
            position: absolute;
            top: 10px;
            right: 10px;
          }
          .theme-toggle-sidebar button {
            padding: 5px 10px;
            cursor: pointer;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 4px;
            color: inherit;
          }
          @media (max-width: 768px) {
            .chatbot-tagline {
              display: none;
            }
          }
        `}
      </style>
      <div className={`confusion-solver-wrapper ${theme}`}>
        <div className="background-container">
          {theme === 'light' ? (
            <div className="morning-sky">
            </div>
          ) : (
            <div className="night-sky">
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="error-message" style={{ color: 'red', padding: '10px' }}>
            {errorMessage}
          </div>
        )}
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="logo">🧠</div>
            {sidebarOpen && <span className="app-name">Confusion Solver</span>}
          </div>
          
          {/* Moved theme toggle to top right inside sidebar */}
          {sidebarOpen && (
            <div className="theme-toggle-sidebar">
              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            </div>
          )}
          
          {sidebarOpen && (
            <>
              <button className="new-chat-btn" onClick={createNewChat}>
                ➕ New Chat
              </button>
              
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="chat-history">
                {filteredSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${currentSessionId === session.id ? 'active' : ''}`}
                    onClick={() => switchToSession(session.id)}
                  >
                    <div className="session-title">{session.title}{session.isTemporary && ' (Unsaved)'}</div>
                    <div className="session-timestamp">
                      {session.timestamp.toLocaleDateString()}
                    </div>
                    <button
                      className="delete-session-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(session.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="main-content">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>

          <div className="chatbot-header">
            <h1 className="chatbot-title">CONFUSION SOLVER</h1>
            <p className="chatbot-tagline">
              <em>Breaking down complex concepts into simple, understandable explanations</em>
            </p>
            <div className="study-focus-header">
              <label className="study-focus-label">Area of Study:</label>
              <div className="study-focus-controls">
                <select
                  value={customAreaOfStudy}
                  onChange={(e) => setCustomAreaOfStudy(e.target.value)}
                  disabled={isLoading}
                  className="study-focus-select"
                >
                  <option value="">Use my profile ({userProfile.area_of_study})</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Physics">Physics</option>
                  <option value="Chemistry">Chemistry</option>
                  <option value="Biology">Biology</option>
                  <option value="Computer Science">Computer Science</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Economics">Economics</option>
                  <option value="Psychology">Psychology</option>
                  <option value="Literature">Literature</option>
                  <option value="History">History</option>
                  <option value="Philosophy">Philosophy</option>
                  <option value="Other">Other (specify below)</option>
                </select>
                
                {customAreaOfStudy === 'Other' && (
                  <input
                    type="text"
                    placeholder="Specify area of study"
                    value={otherAreaOfStudy}
                    onChange={(e) => setOtherAreaOfStudy(e.target.value)}
                    disabled={isLoading}
                    className="other-area-input"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="chat-messages-container" ref={chatContainerRef}>
            {messages.length === 0 ? (
              <div className="welcome-message">
                <div className="welcome-icon">🧠✨</div>
                <h3>Ready to solve your confusion!</h3>
                <p>Tell me what concept you're struggling with, and I'll break it down clearly and simply.</p>
              </div>
            ) : (
              messages.map((message) => (
                <div key={message.id} className="message-wrapper">
                  <ChatMessage 
                    message={message}
                    onSpeak={(text) => speak(text, message.id)}
                    isSpeaking={isMessageSpeaking(message.id)}
                    onEdit={message.role === 'user' ? handleEditMessage : undefined}
                    onTryAgain={message.role === 'assistant' ? handleTryAgain : undefined}
                    onLike={message.role === 'assistant' ? handleLike : undefined}
                    onDislike={message.role === 'assistant' ? handleDislike : undefined}
                    onShare={handleShare}
                    onExportPDF={handleExportPDF}
                  />
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">Thinking...</span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Replaced input section with InputField component */}
          <InputField
            value={input}
            onChange={setInput}
            onSubmit={handleSubmit}
            loading={isLoading}
            placeholder="Tell me what you're struggling to understand..."
          />
        </div>
      </div>
    </>
  );
};

export default ConfusionSolver;